import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../utils/firebase";
import { useAuth } from "../../../context/AuthContext";

const appIcons = require.context("../../../assets/appstore", false, /\.png$/);

interface Project {
  id: string;
  name: string;
  img: string | null;
}

const ICON_MAP: Record<string, string | null> = {
  bland: "bland.png", cali: "cali.png", carni: "carni.png",
  fodmap: "fodmap.png", ifa: "ifa.png", life: "life.png",
  med: "med.png", men: null, pcos: "pcos.png",
  pilates: "pilates.png", rice: "rice.png",
};

const ProjectNav = () => {
  const location = useLocation();
  const { hasProject } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [collapsed, setCollapsed] = useState(false);
  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const snap = await getDocs(collection(db, "content"));
      const loaded: Project[] = [];
      snap.forEach((doc) => {
        const data = doc.data();
        if (data.name) {
          loaded.push({
            id: doc.id,
            name: data.name,
            img: ICON_MAP[doc.id] ?? null
          });
        }
      });
      loaded.sort((a, b) => a.name.localeCompare(b.name));
      setProjects(loaded);
    } catch (err) {
      console.error("[ProjectNav] Error loading projects:", err);
    }
  };

  const pathParts = location.pathname.replace(/^\/cms\/?/, "").split("/");
  const activeProject = pathParts[0] || null;

  /* auto-collapse when a project is selected */
  const prevProject = React.useRef(activeProject);
  useEffect(() => {
    if (activeProject && !prevProject.current) {
      setCollapsed(true);
    }
    prevProject.current = activeProject;
  }, [activeProject]);

  const visibleProjects = projects.filter((p) => hasProject(p.id));

  const handleProjectClick = (id: string) => {
    navigate(`/cms/${id}/insights`);
    setCollapsed(true);
  };

  return (
    <aside className={`cms__sidebar ${collapsed ? "cms__sidebar--collapsed" : ""}`}>
      <div className="cms__sidebar-header">
        <span className="cms__sidebar-title">Projects</span>
        <button
          className="cms__sidebar-toggle"
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d={collapsed
                ? "M6 3l5 5-5 5"
                : "M10 3L5 8l5 5"}
              stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      <nav className="cms__project-list">
        {visibleProjects.map((p) => (
          <button
            key={p.id}
            className={`cms__project-name ${activeProject === p.id ? "cms__project-name--active" : ""}`}
            onClick={() => handleProjectClick(p.id)}
            title={p.name}
          >
            {p.img ? (
              <img
                className="cms__project-icon"
                src={appIcons(`./${p.img}`)}
                alt={p.name}
                width="24"
                height="24"
              />
            ) : (
              <span className="cms__project-icon-placeholder">
                {p.name.charAt(0)}
              </span>
            )}
            <span className="cms__project-label">{p.name}</span>
          </button>
        ))}
      </nav>
    </aside>
  );
};

export default ProjectNav;
